import { useState, useRef, useEffect } from 'react';
import { esvApi } from '../../services/api';
import './AudioPlayer.css';

/**
 * @param {Object} props
 * @param {Function} [props.onChapterFinished] called when the chapter plays to
 *   the end — WS7: listening all the way through counts as reading it.
 */
const AudioPlayer = ({ chapter, onChapterFinished }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  
  const audioRef = useRef(null);
  // Kept in a ref so the 'ended' listener always calls the current callback
  // without having to re-attach on every render.
  const onFinishedRef = useRef(onChapterFinished);
  const finishedChapterRef = useRef(chapter);

  useEffect(() => {
    onFinishedRef.current = onChapterFinished;
    finishedChapterRef.current = chapter;
  }, [onChapterFinished, chapter]);

  // Reset audio state when chapter changes, but don't auto-load
  useEffect(() => {
    if (chapter) {
      setAudioUrl(null);
      setError(null);
      setHasStarted(false);
      setIsLoading(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [chapter]);

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsPaused(false);
      setHasStarted(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
      setIsPaused(true);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      // Never make someone re-enter what the app just watched them do.
      onFinishedRef.current?.(finishedChapterRef.current);
    };

    const handleError = () => {
      setError('Failed to load audio. Please try again.');
      setIsLoading(false);
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // The initial Play click loads the URL; start playback once the element exists.
    audio.play().catch(() => {
      // Browsers may require another gesture after an asynchronous request.
      setIsPlaying(false);
    });

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  const loadAudioUrl = async () => {
    if (!chapter) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await esvApi.getChapterAudio(chapter.id);
      
      if (response.data.success) {
        setAudioUrl(response.data.data.audioUrl);
      } else {
        setError(response.data.error || 'Failed to load audio');
      }
    } catch (err) {
      console.error('Failed to load chapter audio:', err);
      
      let errorMessage = 'Failed to load audio. Please try again.';
      
      if (err.response?.data?.errorType) {
        errorMessage = err.response.data.error || errorMessage;
        
        // Add retry information for rate limiting
        if (err.response.data.errorType === 'rate_limit' && err.response.data.retryAfter) {
          errorMessage += ` (Retry in ${err.response.data.retryAfter} seconds)`;
        }
      } else if (err.response?.status === 429) {
        errorMessage = 'ESV Audio API rate limit exceeded. Please try again in a few minutes.';
      } else if (err.response?.status === 408) {
        errorMessage = 'Audio request timed out. Please check your internet connection.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    
    // If no audio URL, load it first
    if (!audioUrl && !isLoading) {
      await loadAudioUrl();
      return; // loadAudioUrl will trigger a re-render, let that handle the play
    }
    
    if (!audio || !audioUrl) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((err) => {
        console.error('Failed to play audio:', err);
        setError('Failed to play audio. Please try again.');
      });
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  };

  if (!chapter) {
    return null;
  }

  return (
    <div className="audio-player">
      <div className="audio-player-header">
        <div className="audio-info">
          <span className="audio-icon">🎧</span>
          <span className="audio-title">
            Listen to {chapter.book} {chapter.chapter}
          </span>
        </div>
        
        {!hasStarted && !isLoading && !error && !audioUrl && (
          <div className="audio-description">
            <small>Click play to load ESV audio reading</small>
          </div>
        )}
        
        {!hasStarted && !isLoading && !error && audioUrl && (
          <div className="audio-description">
            <small>ESV audio reading ready</small>
          </div>
        )}
      </div>

      {error ? (
        <div className="audio-error">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button onClick={loadAudioUrl} className="retry-button">
            Try again
          </button>
        </div>
      ) : (
        <div className="audio-controls">
          <button
            onClick={togglePlayPause}
            disabled={isLoading}
            className={`play-pause-button ${isPlaying ? 'playing' : ''} ${isLoading ? 'loading' : ''}`}
            title={isLoading ? 'Loading audio...' : isPlaying ? 'Pause' : audioUrl ? 'Play' : 'Load and play audio'}
          >
            {isLoading ? (
              <span className="loading-spinner">⟳</span>
            ) : isPlaying ? (
              <span className="pause-icon">⏸️</span>
            ) : (
              <span className="play-icon">▶️</span>
            )}
          </button>

          {hasStarted && (
            <>
              <div className="time-display">
                <span className="current-time">{formatTime(currentTime)}</span>
                <span className="time-separator">/</span>
                <span className="total-time">{formatTime(duration)}</span>
              </div>

              <div className="progress-container" onClick={handleSeek}>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${getProgressPercentage()}%` }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
        />
      )}
    </div>
  );
};

export default AudioPlayer;
